import { selectSelectedJob } from '../../../../../store/workspace/selectors';
import { CheckListSquareIcon, EditIcon } from '../../../../../assets/icons';
import { getJobDetailThunk } from '../../../../../store/workspace/thunks';
import { thousandSeparator } from '../../../../../utils/numberHelpers';
import { useDispatch, useSelector } from '../../../../../store/store';
import { PostedJob } from '../../../../../store/workspace/types';
import Spinner from '../../../../../components/Spinner';
import Button from '../../../../../components/Button';
import Chips from '../../../../../components/Chips';
import Modal from '../../../../../components/Modal';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import styles from './style.module.scss';
import {
    setConfirmEndExam,
    setJobDetailModal,
    setPreparingExamsModal,
} from '../../../../../store/workspace/slice';

export default function JobDetail() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [job, setJob] = useState<PostedJob | null>(null);
    const selectedJob = useSelector(selectSelectedJob);

    useEffect(() => {
        setIsFetching(true);
        dispatch(getJobDetailThunk({
            job: selectedJob?.id,
        }))
            .unwrap()
            .then((response) => {
                setIsFetching(false);
                setJob(response);
            })
            .catch(() => {
                setIsFetching(false);
            })
    }, []);

    const editJobPost = () => {
        if (!job?.id) return;

        setIsEditing(true);
        dispatch(getJobDetailThunk({ job: job.id }))
            .unwrap()
            .then((freshJob) => {
                setJob(freshJob);

                if ((freshJob.multipleChoiceExam && !freshJob.multipleChoiceExamId) ||
                    (freshJob.codingExam && !freshJob.codingExamId)) {
                    dispatch(setPreparingExamsModal(true));
                    return;
                }

                navigate(`/workspace/edit-job?id=${freshJob.id}`);
                dispatch(setJobDetailModal(false));
            })
            .finally(() => {
                setIsEditing(false);
            });
    };

    return (
        <Modal
            size='xxl'
            title={job?.title || ''}
            icon={<CheckListSquareIcon />}
            childClassName={styles.wrapper}
            closeFunc={() => dispatch(setJobDetailModal(false))}
        >
            {isFetching && <Spinner />}
            {!isFetching && job && <>
                {job.status === 'unposted' &&
                    <div className={styles.actions}>
                        <Button
                            type='Outline-Dark'
                            text='Edit job post'
                            icon={<EditIcon />}
                            iconPosition='Left'
                            className={styles.actions_btn}
                            onClick={editJobPost}
                            loading={isEditing}
                        />
                    </div>}
                <div className={styles.section}>
                    <div className={styles.titleBar}>
                        <span>Job information</span>
                    </div>
                    <div className={styles.info_summary}>
                        <div className={styles.info_summary_item}>
                            <span>Job Title</span>
                            <span>{job.title}</span>
                        </div>
                        <div className={styles.info_summary_item}>
                            <span>Job Base Salary Amount</span>
                            <span>
                                {thousandSeparator(job.baseSalaryAmount || '-')}$ {job.baseSalaryFrequency?.title}
                            </span>
                        </div>
                        <div className={styles.info_summary_item}>
                            <span>Workplace Type</span>
                            <span>{job.workplaceType?.title}</span>
                        </div>
                        <div className={styles.info_summary_item}>
                            <span>Job type</span>
                            <span>{job.jobType?.title}</span>
                        </div>
                        <div className={styles.info_summary_item}>
                            <span>City (Job Location)</span>
                            <span>{job.city?.city}, {job.state?.state} {job.country?.title}</span>
                        </div>
                        <div className={styles.info_summary_description}>
                            <span>Job Description</span>
                            <p>{job.description}</p>
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.titleBar}>
                        <span>Recruiting interview</span>
                    </div>
                    <div className={styles.info_summary}>
                        <div className={styles.info_summary_item}>
                            <span>Generate recruiting interview</span>
                            <span>{job.recruitingInterview ? 'Yes' : 'No'}</span>
                        </div>
                        <div className={styles.info_summary_item}>
                            <span>Recruiting interview questions</span>
                            <div className={styles.questionList}>
                                {job.recruitingInterviewQuestions?.map((item, indx) => <span><b>{indx + 1}-</b> {item}</span>)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.titleBar}>
                        <span>Multiple choice exam</span>
                    </div>
                    <div className={styles.info_summary}>
                        <div className={styles.info_summary_item}>
                            <span>Generate multiple choice exam</span>
                            <span>
                                {job.multipleChoiceExam ? 'Yes' : 'No'}
                            </span>
                        </div>
                        <div className={styles.info_summary_item}>
                            <span>Have coding questions</span>
                            <span>
                                {job.multipleChoiceCodingExam ? 'Yes' : 'No'}
                            </span>
                        </div>
                        <div className={styles.info_summary_item}>
                            <span>Languages for coding questions</span>
                            <div className={styles.arrayList}>
                                {job.multipleChoiceCodingExamLanguages?.length === 0 ? '----' :
                                    job.multipleChoiceCodingExamLanguages?.map(i => <Chips title={i} />)}
                            </div>
                        </div>
                        <div className={styles.info_summary_item}>
                            <span>Skills to cover in greater details:</span>
                            <div className={styles.arrayList}>
                                {job.multipleChoiceExamSkills?.map(i => <Chips title={i} />)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.titleBar}>
                        <span>Coding exam</span>
                    </div>
                    <div className={styles.info_summary}>
                        <div className={styles.info_summary_item}>
                            <span>Generate coding exam</span>
                            <span>{job.codingExam ? 'Yes' : 'No'}</span>
                        </div>
                        <div className={styles.info_summary_item}>
                            <span>Programming languages</span>
                            <div className={styles.arrayList}>
                                {job.codingExamLanguages?.length === 0 ?
                                    '----' : job.codingExamLanguages?.map(i => <Chips title={i} />)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.actions}>
                    {job.status === 'posted' &&
                        <Button
                            text='End job post'
                            type='Error'
                            className={styles.actions_btn}
                            onClick={() => dispatch(setConfirmEndExam(true))}
                        />}
                    <Button
                        text='Close'
                        type='Outline-Dark'
                        className={styles.actions_btn}
                        onClick={() => dispatch(setJobDetailModal(false))}
                    />
                </div>
            </>}
        </Modal>
    );
};
